import React from 'react';
import { ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type HeaderTitleContextValue = {
  content: React.ReactNode | null;
  setContent: (c: React.ReactNode | null) => void;
};
export const HeaderTitleContext = React.createContext<HeaderTitleContextValue>({
  content: null,
  setContent: () => {},
});

export function HeaderTitle({
  children,
}: {
  children: React.ReactNode | null;
}) {
  const { setContent } = React.useContext(HeaderTitleContext);
  React.useEffect(() => {
    setContent(children);
  }, [children, setContent]);
  return null;
}

export function HeaderIconTitle({
  title,
  icon,
}: {
  title: string;
  icon: React.ReactElement<any> | null;
}) {
  const theme = useTheme();
  const Icon: any = icon;
  return (
    <ListItem
      component="div"
      css={{
        width: 'auto',
        color: theme.palette.grey[600],
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      }}
    >
      <ListItemIcon css={{ minWidth: 32 }}>{icon}</ListItemIcon>
      <ListItemText primary={title} />
    </ListItem>
  );
}

export function HeaderTitleRenderer() {
  const { content } = React.useContext(HeaderTitleContext);

  return <>{content}</>;
}

export function HeaderTitleContextContainer(props: {
  children?: React.ReactNode;
}) {
  const [headerTitleContent, setHeaderTitleContent] =
    React.useState<React.ReactNode | null>(null);

  return (
    <HeaderTitleContext.Provider
      value={{
        content: headerTitleContent,
        setContent: setHeaderTitleContent,
      }}
    >
      {props?.children}
    </HeaderTitleContext.Provider>
  );
}

export type UIContextValue = {
  toolbarContent: React.ReactNode | null;
  sidebarOpen: boolean;
  headerOpen: boolean;
  setToolbarContent: (c: React.ReactNode | null) => void;
  setSidebarOpen: (c: boolean) => void;
  setHeaderOpen: (c: boolean) => void;
};

export const UIContext = React.createContext<UIContextValue>({
  toolbarContent: null,
  sidebarOpen: true,
  setToolbarContent: () => {},
  setSidebarOpen: () => {},
  setHeaderOpen: () => {},
  headerOpen: true,
});

export function DisplaySideBar({ show = true }: { show?: boolean }) {
  const { setSidebarOpen } = React.useContext(UIContext);
  React.useEffect(() => {
    setSidebarOpen(show);
    return () => setSidebarOpen(true);
  }, [setSidebarOpen, show]);
  return null;
}

export function DisplayHeader({ show = true }: { show?: boolean }) {
  const { setHeaderOpen } = React.useContext(UIContext);
  React.useEffect(() => {
    setHeaderOpen(show);
    return () => setHeaderOpen(true);
  }, [setHeaderOpen, show]);
  return null;
}

export function UIContextContainer(props: { children?: React.ReactNode }) {
  const [UIContent, setUIContent] = React.useState<React.ReactNode | null>(
    null
  );
  const [sidebarOpen, setSidebarOpen] = React.useState<boolean>(true);
  const [headerOpen, setHeaderOpen] = React.useState<boolean>(true);

  return (
    <UIContext.Provider
      value={{
        toolbarContent: UIContent,
        setToolbarContent: setUIContent,
        setSidebarOpen,
        sidebarOpen,
        headerOpen,
        setHeaderOpen,
      }}
    >
      {props?.children}
    </UIContext.Provider>
  );
}
